import { useEffect, useRef, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ShieldCheck, Truck, Warehouse, CircleDollarSign, ArrowRight, Banknote } from 'lucide-react';

const PETSBOX_LOGO = "https://customer-assets.emergentagent.com/job_paws-box/artifacts/gyymiifm_f89199bc-32c1-4646-a02f-5de9041d5b0a.png";

const FAN_COURIER_LOGO = "https://customer-assets.emergentagent.com/job_paws-box/artifacts/9saq7b6p_0507-fan-courier.png";
const DPD_LOGO = "/assets/dpd_logo.svg";
const VISA_LOGO = "/assets/visa_logo.svg";
const MASTERCARD_LOGO = "/assets/mastercard_logo.svg";
const PARTNER_LOGOS = "/assets/partner_logos.png";

const RO_FLAG = "\u{1F1F7}\u{1F1F4}";
const BG_FLAG = "\u{1F1E7}\u{1F1EC}";

const PET_PHOTOS = [
  "/assets/clients/dog2_frenchie.jpg",
  "/assets/clients/dog3_pug.jpg",
  "/assets/clients/dog1_box.jpg",
  "/assets/clients/cat_boxes.jpg",
  "https://customer-assets.emergentagent.com/job_3bf0b3a7-b977-4048-8ca7-1a63aced161d/artifacts/c06xvil6_IMG_1008.jpg",
  "https://customer-assets.emergentagent.com/job_3bf0b3a7-b977-4048-8ca7-1a63aced161d/artifacts/v57g76qx_IMG_1007.jpg",
  "https://customer-assets.emergentagent.com/job_3bf0b3a7-b977-4048-8ca7-1a63aced161d/artifacts/sfg23did_IMG_1006.jpg",
  "https://customer-assets.emergentagent.com/job_3bf0b3a7-b977-4048-8ca7-1a63aced161d/artifacts/xplsdoqm_IMG_1005.jpg",
  "https://customer-assets.emergentagent.com/job_3bf0b3a7-b977-4048-8ca7-1a63aced161d/artifacts/5611iah6_IMG_1004.jpg",
];

const PET_ALTS = [
  "Happy dog with PetsBox", "Playful pug", "Dog opening box", "Cat with deliveries",
  "Cat with PetsBox treats", "French bulldog with PetsBox", "Dog with PetsBox on blanket",
  "Cute poodle with PetsBox", "Dog with full PetsBox contents",
];

function LogoBadges({ className = "" }) {
  return (
    <div className={`flex items-center ${className}`}>
      <img src={PARTNER_LOGOS} alt="DPD Romania, Visa, Mastercard" className="h-12 object-contain" />
    </div>
  );
}

function MobileCarousel({ photos }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let idx = 0;
    const interval = setInterval(() => {
      idx = (idx + 1) % photos.length;
      const child = el.children[idx];
      if (child) {
        el.scrollTo({ left: child.offsetLeft - el.offsetLeft, behavior: 'smooth' });
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [photos.length]);

  return (
    <div
      ref={scrollRef}
      className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-3 -mx-4 px-4"
      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
    >
      {photos.map((photo, i) => (
        <div
          key={i}
          className="flex-shrink-0 w-[72%] snap-center rounded-2xl overflow-hidden shadow-lg"
        >
          <img
            src={photo.src}
            alt={photo.alt}
            className="w-full aspect-[4/3] object-cover"
            data-testid={`hero-image-${i + 1}`}
          />
        </div>
      ))}
    </div>
  );
}

export default function HeroSection({ onSubscribeClick }) {
  const { t, isRomanian, getBasePrice } = useLanguage();

  // Shuffle photos randomly on each mount
  const shuffledPhotos = useMemo(() => {
    const paired = PET_PHOTOS.map((src, i) => ({ src, alt: PET_ALTS[i] || 'Pet with PetsBox' }));
    for (let i = paired.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [paired[i], paired[j]] = [paired[j], paired[i]];
    }
    return paired;
  }, []);

  return (
    <section className="lg:min-h-screen pt-44 bg-brand-cream relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-40 left-10 w-64 h-64 bg-brand-orange/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-brand-brown/5 rounded-full blur-3xl" />
      
      <div className="section-container py-4 lg:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-12 items-center">

          {/* ===== TEXT CONTENT ===== */}
          <div className="lg:col-span-6 space-y-3 lg:space-y-4 text-center lg:text-left order-2 lg:order-1">
            {/* Price Tag — MOBILE ONLY (above heading) */}
            <div className="flex items-center gap-3 justify-center flex-wrap lg:hidden">
              <div className="bg-brand-brown text-white px-5 py-3 rounded-2xl">
                <p className="text-xl sm:text-2xl font-bold">
                  <span className="line-through opacity-50 text-base font-normal mr-1">
                    {isRomanian ? '150 lei' : '\u20AC30'}
                  </span>
                  {isRomanian ? `${getBasePrice()} lei` : `\u20AC${getBasePrice()}`}
                  <span className="text-sm font-normal opacity-80">{t('pricing_per_month')}</span>
                </p>
              </div>
              <div className="bg-brand-success/20 text-brand-success px-4 py-2 rounded-xl text-sm font-semibold">
                {t('promo_badge')}
              </div>
            </div>

            <div className="mt-2">
              <div className="inline-flex items-center gap-2 px-5 py-2 bg-brand-orange lg:bg-brand-brown text-white rounded-full shadow-lg animate-bounce" style={{animationDuration: '2s'}}>
                <span className="text-base">{'\u{1F1F7}\u{1F1F4}'}</span>
                <span className="text-sm font-bold tracking-wide uppercase">
                  {isRomanian ? 'ACUM \u00ceN ROM\u00c2NIA!' : 'NOW IN ROMANIA!'}
                </span>
              </div>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-brand-brown leading-tight">
              {t('hero_title_1')}<br />
              <span className="text-brand-orange">{t('hero_title_2')}</span><br />
              <span className="text-brand-orange">{t('hero_title_3')}</span>
            </h1>
            
            <p className="text-base lg:text-xl text-brand-muted max-w-xl mx-auto lg:mx-0">
              {t('hero_subtitle')}
            </p>

            {/* Price Tag — DESKTOP ONLY (below subtitle, before CA) */}
            <div className="hidden lg:flex items-center gap-3 justify-start flex-wrap">
              <div className="bg-brand-brown text-white px-5 py-3 rounded-2xl">
                <p className="text-xl sm:text-2xl font-bold">
                  <span className="line-through opacity-50 text-base font-normal mr-1">
                    {isRomanian ? '150 lei' : '\u20AC30'}
                  </span>
                  {isRomanian ? `${getBasePrice()} lei` : `\u20AC${getBasePrice()}`}
                  <span className="text-sm font-normal opacity-80">{t('pricing_per_month')}</span>
                </p>
              </div>
              <div className="bg-brand-success/20 text-brand-success px-4 py-2 rounded-xl text-sm font-semibold">
                {t('promo_badge')}
              </div>
            </div>
            
            {/* CTA Button — attractive */}
            <div className="flex flex-col items-center lg:items-start gap-3">
              <button
                onClick={onSubscribeClick}
                className="group relative bg-brand-orange text-white rounded-full px-10 py-5 text-lg sm:text-xl font-bold
                           shadow-[0_6px_24px_rgba(253,102,51,0.45)]
                           hover:shadow-[0_8px_32px_rgba(253,102,51,0.6)] hover:-translate-y-1
                           active:translate-y-0 transition-all duration-200
                           ring-4 ring-brand-orange/20"
                data-testid="hero-cta-primary"
              >
                <span className="flex items-center gap-2">
                  {t('hero_cta')}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              {/* Mini social proof */}
              <div className="flex items-center gap-2 text-sm text-brand-muted">
                <div className="flex -space-x-2">
                  <div className="w-7 h-7 rounded-full bg-brand-orange/20 border-2 border-white flex items-center justify-center text-xs">{'\u{1F436}'}</div>
                  <div className="w-7 h-7 rounded-full bg-brand-success/20 border-2 border-white flex items-center justify-center text-xs">{'\u{1F431}'}</div>
                  <div className="w-7 h-7 rounded-full bg-brand-brown/20 border-2 border-white flex items-center justify-center text-xs">{'\u{1F43E}'}</div>
                </div>
                <span className="font-semibold text-brand-brown">5100+</span>
                <span>{isRomanian ? 'cutii trimise' : 'boxes delivered'}</span>
              </div>
            </div>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-2.5 justify-center lg:justify-start pt-2">
              <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-brand-border shadow-sm">
                <ShieldCheck className="w-4 h-4 text-brand-success" />
                <span className="text-xs text-brand-brown font-medium">{t('trust_secure')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-brand-border shadow-sm">
                <Truck className="w-4 h-4 text-brand-orange" />
                <span className="text-xs text-brand-brown font-medium">{t('trust_shipping')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-brand-border shadow-sm">
                <CircleDollarSign className="w-4 h-4 text-brand-success" />
                <span className="text-xs text-brand-brown font-medium">{isRomanian ? 'Garanție ramburs' : 'Money-back guarantee'}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-brand-border shadow-sm">
                <Banknote className="w-4 h-4 text-amber-600" />
                <span className="text-xs text-brand-brown font-medium">{isRomanian ? 'Plata prin ramburs' : 'Cash on delivery'}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-brand-border shadow-sm">
                <Warehouse className="w-4 h-4 text-brand-brown" />
                <span className="text-base">{RO_FLAG}</span>
                <span className="text-base">{BG_FLAG}</span>
              </div>
            </div>

            {/* Courier & Payment Logos */}
            <LogoBadges className="justify-center lg:justify-start bg-white/50 px-4 py-2 rounded-xl inline-flex" />
          </div>
          
          {/* ===== IMAGES ===== */}
          <div className="lg:col-span-6 relative order-1 lg:order-2">

            {/* Mobile: horizontal carousel */}
            <div className="lg:hidden">
              <MobileCarousel photos={shuffledPhotos} />
            </div>

            {/* Desktop: 2×2 grid (first 4 shuffled photos) */}
            <div className="hidden lg:block">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-3">
                  <div className="rounded-[2rem] overflow-hidden shadow-xl">
                    <img src={shuffledPhotos[0]?.src} alt={shuffledPhotos[0]?.alt} className="w-full aspect-[4/5] object-cover hover:scale-105 transition-transform duration-500" data-testid="hero-image-1" />
                  </div>
                  <div className="rounded-[2rem] overflow-hidden shadow-xl">
                    <img src={shuffledPhotos[1]?.src} alt={shuffledPhotos[1]?.alt} className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-500" data-testid="hero-image-2" />
                  </div>
                </div>
                <div className="space-y-3 pt-8">
                  <div className="rounded-[2rem] overflow-hidden shadow-xl">
                    <img src={shuffledPhotos[2]?.src} alt={shuffledPhotos[2]?.alt} className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-500" data-testid="hero-image-3" />
                  </div>
                  <div className="rounded-[2rem] overflow-hidden shadow-xl">
                    <img src={shuffledPhotos[3]?.src} alt={shuffledPhotos[3]?.alt} className="w-full aspect-[4/5] object-cover hover:scale-105 transition-transform duration-500" data-testid="hero-image-4" />
                  </div>
                </div>
              </div>
              
              {/* Floating badges — desktop only */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-lg border border-brand-border/50 z-10">
                <p className="font-accent text-xl text-brand-orange">5-7 {isRomanian ? 'produse' : 'products'}</p>
                <p className="text-sm text-brand-muted">{isRomanian ? 'in fiecare cutie' : 'in every box'}</p>
              </div>
              <div className="absolute -top-2 -right-2 bg-brand-orange rounded-2xl p-4 shadow-lg z-10">
                <p className="font-heading text-lg font-bold text-white">{'\u{1F415}'} + {'\u{1F408}'}</p>
                <p className="text-xs text-white/90">{isRomanian ? 'Pentru caini si pisici' : 'For dogs and cats'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Wave separator */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
}

export { PETSBOX_LOGO, FAN_COURIER_LOGO, DPD_LOGO, VISA_LOGO, MASTERCARD_LOGO, LogoBadges };
